import { Alert } from '@stratascratch/ui'
import { AlgorithmExecutionResults } from '@stratascratch/code-editor'

interface Props {
  results: AlgorithmExecutionResults
}

function SolutionResults({ results }: Props) {
  const executionOutput = results?.captured_output
  const isCorrect = results?.is_correct
  const failedTests = results?.failed_tests

  return (
    <>
      <h4 className="text-lg font-bold">Output</h4>

      {executionOutput && <pre className="mt-4 px-4">{executionOutput}</pre>}

      {isCorrect === false && (
        <Alert type="danger" noIcon className="flex-col">
          <p>Solution is incorrect.</p>
          <p>
            For a solution to be accepted, your output for every test should
            match the output of the official solution.
          </p>
        </Alert>
      )}
      {isCorrect === true && (
        <Alert
          type="success"
          noIcon
          className="QuestionResults__correct-message"
        >
          Solution is correct! All tests passed.
        </Alert>
      )}

      {failedTests && (
        <>
          <h4 className="my-4 text-lg font-bold">Failed Test Cases</h4>
          <div className="flex flex-col gap-4">
            {failedTests?.map((test) => (
              <div
                key={test.id}
                className="bg-(--bg-color-secondary) flex flex-col gap-2 rounded-md p-4"
              >
                <p className="text-base font-bold">Test {test.id}</p>
                <div className="text-sm">
                  <p className="font-bold">Input:</p>
                  <p>{test.input}</p>
                </div>
                <div className="text-sm">
                  <p className="font-bold">Your output:</p>
                  <p>{test.user_output}</p>
                </div>
                <div className="text-sm">
                  <p className="font-bold">Official output: </p>
                  <p>{test.expected_output}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function TestResults({ results }: Props) {
  const executionOutput = results?.captured_output
  const tableResults = results?.user_results?.results
  const isCorrect = results?.is_correct

  return (
    <>
      {executionOutput && <pre className="mt-4 px-4">{executionOutput}</pre>}

      {!executionOutput && tableResults === null && (
        <Alert type="info">No result</Alert>
      )}

      {executionOutput && <pre className="mt-4 px-4">{executionOutput}</pre>}

      {isCorrect === true && (
        <Alert type="success" className="QuestionResults__correct-message">
          Your output is correct!
        </Alert>
      )}
      {isCorrect === false && (
        <div>
          <Alert type="danger" className="QuestionResults__incorrect-message">
            Your output is incorrect.
          </Alert>
          <div className="bg-(--bg-color-secondary) flex flex-col gap-2 rounded-md p-4">
            <div className="text-sm">
              <p className="font-bold">Your output:</p>
              <p>{results.results}</p>
            </div>
            <div className="text-sm">
              <p className="font-bold">Official output: </p>
              <p>{results.author_results}</p>
            </div>
          </div>
        </div>
      )}
      {tableResults && (
        <div>
          <h4 className="mt-4 text-base font-bold">Your output:</h4>
          <pre className="my-4">{JSON.stringify(tableResults)}</pre>
        </div>
      )}
    </>
  )
}

export default function AlgorithmResults({ results }: Props) {
  const executionError = results?.error

  if (executionError) {
    return (
      <Alert type="danger" noIcon>
        {executionError}
      </Alert>
    )
  }

  if (results.is_solution_submission) {
    return <SolutionResults results={results} />
  }

  return <TestResults results={results} />
}
